import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useDocumentsStore } from '@/stores/documents-store'
import {
  getAcceptedFileTypes,
  normalizeFilename,
  formatFileSize,
} from '@/lib/schemas'
import { ARCHIVE_ACCEPT_TYPES } from '@/lib/expand-upload-files'
import { prepareUploadsWithFeedback } from '@/lib/upload-feedback'
import { useFolderPicker } from '@/hooks/useFolderPicker'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface DuplicateFileInfo {
  file: File
  reason: string
}

/**
 * Exported for reuse + unit testing. Compares incoming files against existing
 * document names AND against each other within the batch.
 */
export function findDuplicates(
  newFiles: File[],
  existingDocumentNames: string[]
): Map<number, DuplicateFileInfo> {
  const duplicates = new Map<number, DuplicateFileInfo>()
  const normalizedNew = newFiles.map((f) => normalizeFilename(f.name))
  const normalizedExisting = existingDocumentNames.map((n) => normalizeFilename(n))

  for (let i = 0; i < newFiles.length; i++) {
    const file = newFiles[i]!
    const normalizedName = normalizedNew[i]!

    const existingMatch = existingDocumentNames.find(
      (_, idx) => normalizedExisting[idx] === normalizedName
    )
    if (existingMatch) {
      duplicates.set(i, { file, reason: `Already uploaded as "${existingMatch}"` })
      continue
    }

    for (let j = 0; j < i; j++) {
      if (normalizedNew[j] === normalizedName) {
        duplicates.set(i, { file, reason: `Same document as "${newFiles[j]!.name}"` })
        break
      }
    }
  }

  return duplicates
}

interface UseBidDocumentUploadOptions {
  bidId: string
  disabled?: boolean
}

interface UseBidDocumentUploadResult {
  /** Programmatically open the native file picker (used by the "Upload files" menu item). */
  openFilePicker: () => void
  /** Open the native folder picker (used by the "Upload folder" menu item). */
  openFolderPicker: () => void
  /** Open ZIPs + validate + duplicate-check + upload. Used by drop targets. */
  handleFiles: (files: File[]) => void
  /** True while an upload is in flight. */
  isUploading: boolean
  /** Render this somewhere in the page tree — it owns the duplicate confirmation dialog
   *  and the hidden <input type="file">s the pickers use. */
  uploadDialog: ReactNode
}

/**
 * Encapsulates everything needed to add documents to a bid:
 *  - native file-picker trigger
 *  - shared validation + duplicate detection
 *  - the duplicate-confirmation dialog
 *
 * Used by both the "Upload files" menu item and the drag-overlay.
 */
export function useBidDocumentUpload({
  bidId,
  disabled = false,
}: UseBidDocumentUploadOptions): UseBidDocumentUploadResult {
  const { uploadTenderDocuments, uploadingFiles, documents } = useDocumentsStore()

  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [duplicateMap, setDuplicateMap] = useState<Map<number, DuplicateFileInfo>>(
    new Map()
  )
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set())
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const isUploading = uploadingFiles.some(
    (f) => f.status === 'uploading' || f.status === 'pending'
  )

  const handleFiles = useCallback(
    (files: File[]) => {
      if (disabled) return

      void (async () => {
        // Opens any ZIP, drops system files, applies `validateFile`, and names
        // anything it left out.
        const validFiles = await prepareUploadsWithFeedback(files)
        if (validFiles.length === 0) return

        const existingNames = documents.map((d) => d.name)
        const dups = findDuplicates(validFiles, existingNames)

        if (dups.size > 0) {
          setPendingFiles(validFiles)
          setDuplicateMap(dups)
          setExcludedIndices(new Set(dups.keys()))
          setShowDuplicateDialog(true)
        } else {
          void uploadTenderDocuments(bidId, validFiles)
        }
      })()
    },
    [bidId, disabled, documents, uploadTenderDocuments]
  )

  const { openFolderPicker, folderInput } = useFolderPicker({
    onFiles: handleFiles,
    disabled: disabled || isUploading,
  })

  const openFilePicker = useCallback(() => {
    if (disabled) return
    fileInputRef.current?.click()
  }, [disabled])

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : []
      // reset so the same file can be picked again
      e.target.value = ''
      if (files.length > 0) handleFiles(files)
    },
    [handleFiles]
  )

  const handleDialogConfirm = () => {
    const filesToUpload = pendingFiles.filter((_, idx) => !excludedIndices.has(idx))
    setShowDuplicateDialog(false)
    setPendingFiles([])
    setDuplicateMap(new Map())
    setExcludedIndices(new Set())
    if (filesToUpload.length > 0) {
      void uploadTenderDocuments(bidId, filesToUpload)
    }
  }

  const handleDialogCancel = () => {
    setShowDuplicateDialog(false)
    setPendingFiles([])
    setDuplicateMap(new Map())
    setExcludedIndices(new Set())
  }

  const toggleFile = (index: number) => {
    setExcludedIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const selectedCount = pendingFiles.length - excludedIndices.size

  const acceptString = useMemo(() => {
    return Object.entries({ ...getAcceptedFileTypes(), ...ARCHIVE_ACCEPT_TYPES })
      .flatMap(([mime, exts]) => [mime, ...exts])
      .join(',')
  }, [])

  const uploadDialog = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptString}
        className="hidden"
        onChange={onInputChange}
        disabled={disabled || isUploading}
        data-testid="bid-document-upload-input"
      />
      {folderInput}

      <Dialog
        open={showDuplicateDialog}
        onOpenChange={(open) => !open && handleDialogCancel()}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Potential Duplicate Documents
            </DialogTitle>
            <DialogDescription>
              Some files appear to be duplicates. Uploading duplicates may affect
              analysis quality. Uncheck files you want to skip.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[300px] overflow-y-auto space-y-1 py-2">
            {pendingFiles.map((file, index) => {
              const dupInfo = duplicateMap.get(index)
              const isExcluded = excludedIndices.has(index)

              return (
                <label
                  key={`${file.name}-${index}`}
                  className={cn(
                    'flex items-start gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
                    isExcluded ? 'opacity-50' : 'hover:bg-accent/50'
                  )}
                >
                  <Checkbox
                    checked={!isExcluded}
                    onCheckedChange={() => toggleFile(index)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileTypeIcon mimeType={file.type} size={18} />
                      <span
                        className={cn(
                          'text-sm truncate',
                          isExcluded && 'line-through'
                        )}
                      >
                        {file.name}
                      </span>
                      <span className="text-meta flex-shrink-0">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    {dupInfo && (
                      <div className="mt-1 ml-6">
                        <Badge variant="warning" className="text-[10px]">
                          {dupInfo.reason}
                        </Badge>
                      </div>
                    )}
                  </div>
                </label>
              )
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleDialogCancel}>
              Cancel
            </Button>
            <Button onClick={handleDialogConfirm} disabled={selectedCount === 0}>
              Upload {selectedCount} {selectedCount === 1 ? 'file' : 'files'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  return {
    openFilePicker,
    openFolderPicker,
    handleFiles,
    isUploading,
    uploadDialog,
  }
}
